import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BookOpen, Users, Clock, Award, Play, Star, Plus, Search, CheckCircle, Video, FileText, BarChart3, Target, Upload, MessageSquare, Edit, Eye, Trophy, Bookmark, Share2, Filter, TrendingUp, Zap, Mic, Users2, BookOpenCheck, RefreshCw, X, Heart, MoreVertical, CreditCard } from 'lucide-react';
import useMobileOptimization from '../../hooks/useMobileOptimization';
import { collection, getDocs, query, where, orderBy, limit, addDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useRealtime } from '../../contexts/RealtimeContext';
import { getLectures } from '../../firebase/collections';
import { convertToEmbedUrl, isValidVideoUrl } from '../../utils/videoUtils';
import VideoPlayer from '../../components/VideoPlayer';
import { formatTimestamp } from '../../utils/timestampUtils';
import SmartPaymentModal from '../../components/payment/SmartPaymentModal';
import toast from 'react-hot-toast';
import { StudyStreakManager } from '../../utils/studyStreakManager';
import { getUserPracticeSessions } from '../../firebase/collections';

const Courses = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useRealtime();
  const { isMobile, isTablet, mobileUtils } = useMobileOptimization();
  const user = currentUser; // Alias for currentUser
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [showCatalog, setShowCatalog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedLevel, setSelectedLevel] = useState('all');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDetails, setShowCourseDetails] = useState(false);
  const [currentLesson, setCurrentLesson] = useState(0);
  const [courseDetailTab, setCourseDetailTab] = useState('content');
  const [showAssignments, setShowAssignments] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showNewAssignment, setShowNewAssignment] = useState(false);
  const [courseDiscussions, setCourseDiscussions] = useState({});
  const [showCertificates, setShowCertificates] = useState(false);
  const [showStudyMaterials, setShowStudyMaterials] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showAssignmentSubmission, setShowAssignmentSubmission] = useState(false);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [submissionText, setSubmissionText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostTags, setNewPostTags] = useState('');
  const [error, setError] = useState('');
  
  // Lecture Management State
  const [courseLectures, setCourseLectures] = useState([]);
  const [loadingLectures, setLoadingLectures] = useState(false);
  
  // Video Player State
  const [showVideoPlayer, setShowVideoPlayer] = useState(false);
  const [currentLecture, setCurrentLecture] = useState(null);
  const [lectureBookmarks, setLectureBookmarks] = useState([]);
  const [completedVideos, setCompletedVideos] = useState(new Set());
  
  // Discussion State
  const [showDiscussion, setShowDiscussion] = useState(false);

  // Payment State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  // Analytics Data State
  const [analyticsData, setAnalyticsData] = useState({
    studyTimeDistribution: [],
    performanceMetrics: {
      averageScore: 0,
      studyHours: 0,
      lessonsCompleted: 0,
      assignmentsDone: 0
    },
    learningStreak: 0,
    streakHistory: []
  });

  // Real course data from Firebase
  const [coursesData, setCoursesData] = useState({
    stats: {
      enrolledCourses: 0,
      completedCourses: 0,
      totalHours: 0,
      averageScore: 0,
      certificates: 0
    },
    enrolledCourses: [],
    assignments: [],
    availableCourses: [],
    certificates: [],
    studyMaterials: [],
    liveClasses: [],
    notifications: [],
    upcomingClasses: []
  });

  // Load courses from Firebase
  useEffect(() => {
    if (currentUser?.uid) {
      loadCoursesFromFirebase();
      loadAnalyticsData();
    }
  }, [currentUser?.uid]);

  // Video Player Functions
  const playLecture = (lecture) => {
    setCurrentLecture(lecture);
    setShowVideoPlayer(true);
  };

  const closeVideoPlayer = () => {
    setShowVideoPlayer(false);
    setCurrentLecture(null);
    setShowDiscussion(false);
  };

  const handleAddBookmark = (bookmark) => {
    setLectureBookmarks([...lectureBookmarks, bookmark]);
  };

  const handleTimeUpdate = (currentTime) => {
    // You can implement progress tracking here
    // Current time updated
  };

  const handleVideoComplete = async (lectureId) => {
    // Mark video as completed
    setCompletedVideos(prev => new Set([...prev, lectureId]));
    
    // Find the lecture index
    const lectureIndex = courseLectures.findIndex(lecture => lecture.id === lectureId);
    if (lectureIndex === -1) return;
    
    // Only update progress if this video wasn't already completed
    if (!completedVideos.has(lectureId)) {
      const newCompletedLessons = Math.min(selectedCourse.completedLessons + 1, courseLectures.length);
      const newProgress = Math.min(100, Math.round((newCompletedLessons / courseLectures.length) * 100));
      
      await saveCourseProgress(selectedCourse.id, {
        completedLessons: newCompletedLessons,
        progress: newProgress
      });
    }
  };

  const handleVideoEnd = () => {
    // Video ended
    // Mark lecture as completed
    if (currentLecture) {
      // Update completion status in Firebase
      // Marking lecture as completed
    }
  };

  const loadCoursesFromFirebase = async () => {
    try {
      setLoading(true);
      // Load available courses
      const coursesRef = collection(db, 'courses');
      const coursesSnapshot = await getDocs(coursesRef);
      const availableCourses = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load enrolled courses for the current user
      const enrolledCoursesQuery = query(
        collection(db, 'userCourses'),
        where('userId', '==', currentUser.uid),
        orderBy('enrolledAt', 'desc')
      );
      const enrolledCoursesSnapshot = await getDocs(enrolledCoursesQuery);
      
      // Create a set of available course IDs for quick lookup
      const availableCourseIds = new Set(availableCourses.map(course => course.id));
      
      // Filter out enrolled courses that no longer exist in the courses collection
      // The enrollment document ID format is: userId_courseId
      const enrolledCourses = enrolledCoursesSnapshot.docs
        .map(doc => {
          const data = doc.data();
          // Extract courseId from document ID (format: userId_courseId)
          const docIdParts = doc.id.split('_');
          let courseId = null;
          
          if (docIdParts.length > 1 && docIdParts[0] === currentUser.uid) {
            // Extract courseId from document ID
            courseId = docIdParts.slice(1).join('_');
          } else if (data.courseId) {
            // Use courseId from data if available
            courseId = data.courseId;
          } else if (data.id && availableCourseIds.has(data.id)) {
            // Use id field if it's a valid course ID
            courseId = data.id;
          }
          
          // Check if this course still exists
          if (courseId && availableCourseIds.has(courseId)) {
            // Find the actual course data to merge with enrollment data
            const actualCourse = availableCourses.find(c => c.id === courseId);
            
            return {
              ...data,
              id: courseId, // Use courseId as the id for consistency
              courseId: courseId, // Store courseId for reference
              // Merge with actual course data to get latest info
              title: actualCourse?.title || data.title,
              instructor: actualCourse?.instructor || data.instructor,
              instructorAvatar: actualCourse?.instructorAvatar || data.instructorAvatar,
              thumbnail: actualCourse?.thumbnail || data.thumbnail,
              level: actualCourse?.level || data.level,
              rating: actualCourse?.rating || data.rating,
              students: actualCourse?.students || data.students,
              duration: actualCourse?.duration || data.duration
            };
          }
          
          // Course was deleted - return null to filter it out
          return null;
        })
        .filter(course => course !== null); // Remove deleted courses

      // Load assignments
      const assignmentsRef = collection(db, 'assignments');
      const assignmentsSnapshot = await getDocs(assignmentsRef);
      const assignments = assignmentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load study materials
      const materialsRef = collection(db, 'studyMaterials');
      const materialsSnapshot = await getDocs(materialsRef);
      const studyMaterials = materialsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Load live classes
      const liveClassesRef = collection(db, 'liveClasses');
      const liveClassesSnapshot = await getDocs(liveClassesRef);
      const liveClasses = liveClassesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Get upcoming live classes (scheduled in future)
      const now = new Date();
      const upcomingLiveClasses = liveClasses
        .filter(lc => {
          const scheduledTime = lc.scheduledAt?.toDate ? lc.scheduledAt.toDate() : new Date(lc.scheduledAt || lc.startTime);
          return scheduledTime > now && lc.status !== 'completed' && lc.status !== 'cancelled';
        })
        .sort((a, b) => {
          const timeA = a.scheduledAt?.toDate ? a.scheduledAt.toDate() : new Date(a.scheduledAt || a.startTime);
          const timeB = b.scheduledAt?.toDate ? b.scheduledAt.toDate() : new Date(b.scheduledAt || b.startTime);
          return timeA - timeB;
        })
        .slice(0, 5); // Get next 5 upcoming classes

      // Map upcoming classes to enrolled courses
      const upcomingClasses = enrolledCourses
        .map(course => {
          const upcomingClass = upcomingLiveClasses.find(lc => 
            lc.courseId === course.id || 
            lc.course === course.title || 
            lc.courseName === course.title
          );
          
          if (upcomingClass) {
            const scheduledTime = upcomingClass.scheduledAt?.toDate ? 
              upcomingClass.scheduledAt.toDate() : 
              new Date(upcomingClass.scheduledAt || upcomingClass.startTime);
            
            const formatDateTime = (date) => {
              const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
              const dayName = days[date.getDay()];
              const hours = date.getHours();
              const minutes = date.getMinutes();
              const ampm = hours >= 12 ? 'PM' : 'AM';
              const displayHours = hours % 12 || 12;
              const displayMinutes = minutes.toString().padStart(2, '0');
              
              // Check if it's today, tomorrow, or a specific day
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const classDate = new Date(date);
              classDate.setHours(0, 0, 0, 0);
              
              const diffTime = classDate - today;
              const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
              
              if (diffDays === 0) return `Today, ${displayHours}:${displayMinutes} ${ampm}`;
              if (diffDays === 1) return `Tomorrow, ${displayHours}:${displayMinutes} ${ampm}`;
              if (diffDays < 7) return `${dayName}, ${displayHours}:${displayMinutes} ${ampm}`;
              return `${dayName}, ${date.toLocaleDateString()} ${displayHours}:${displayMinutes} ${ampm}`;
            };
            
            return {
              ...course,
              nextClass: formatDateTime(scheduledTime),
              nextClassTime: scheduledTime,
              liveClassId: upcomingClass.id
            };
          }
          return null;
        })
        .filter(course => course !== null)
        .sort((a, b) => a.nextClassTime - b.nextClassTime);

      // Calculate real study hours from practice sessions and watched minutes
      let totalStudyHours = 0;
      try {
        // Get practice sessions
        const practiceSessionsResult = await getUserPracticeSessions(currentUser.uid, 100);
        const practiceSessions = practiceSessionsResult.data || [];
        const practiceHours = practiceSessions.reduce((total, session) => {
          const duration = session.duration || session.studyTime || 0; // in minutes
          return total + (duration / 60); // Convert to hours
        }, 0);
        
        // Get watched minutes from enrolled courses
        const watchedHours = enrolledCourses.reduce((total, course) => {
          const watchedMinutes = course.watchedMinutes || 0;
          return total + (watchedMinutes / 60); // Convert to hours
        }, 0);
        
        totalStudyHours = practiceHours + watchedHours;
      } catch (error) {
        // Fallback to 0 if calculation fails
        totalStudyHours = 0;
      }

      // Calculate stats
      const stats = {
        enrolledCourses: enrolledCourses.length,
        completedCourses: enrolledCourses.filter(course => course.status === 'completed').length,
        totalHours: Math.round(totalStudyHours * 10) / 10, // Round to 1 decimal place
        averageScore: enrolledCourses.length > 0 ? 
          enrolledCourses.reduce((total, course) => total + (course.progress || 0), 0) / enrolledCourses.length : 0,
        certificates: enrolledCourses.filter(course => course.status === 'completed').length
      };

      // Use available courses or empty array if none available
      const coursesWithLectures = availableCourses.length > 0 ? availableCourses : [];

      setCoursesData({
        stats,
        enrolledCourses,
        assignments,
        availableCourses: coursesWithLectures,
        certificates: [],
        studyMaterials,
        liveClasses,
        notifications: [],
        upcomingClasses
      });

      } catch (error) {
      setError('Failed to load courses. Please try again later.');
      setCoursesData({
        stats: {
          enrolledCourses: 0,
          completedCourses: 0,
          totalHours: 0,
          averageScore: 0,
          certificates: 0
        },
        enrolledCourses: [],
        assignments: [],
        availableCourses: [],
        certificates: [],
        studyMaterials: [],
        liveClasses: [],
        notifications: [],
        upcomingClasses: []
      });
    } finally {
      setLoading(false);
    }
  };

  // Load real analytics data
  const loadAnalyticsData = async () => {
    if (!currentUser?.uid) return;
    
    try {
      // Load study time distribution from practice sessions
      const practiceSessionsResult = await getUserPracticeSessions(currentUser.uid, 100);
      const practiceSessions = practiceSessionsResult.data || [];
      
      // Calculate study time by subject/category
      const studyTimeByCategory = {};
      practiceSessions.forEach(session => {
        const category = session.category || session.subject || session.topic || 'General';
        const duration = session.duration || session.studyTime || 0; // in minutes
        studyTimeByCategory[category] = (studyTimeByCategory[category] || 0) + duration;
      });
      
      // Convert to hours and sort
      const studyTimeDistribution = Object.entries(studyTimeByCategory)
        .map(([category, minutes]) => ({
          category,
          hours: Math.round((minutes / 60) * 10) / 10 // Round to 1 decimal
        }))
        .sort((a, b) => b.hours - a.hours)
        .slice(0, 10); // Top 10 categories

      // Calculate performance metrics from enrolled courses
      const enrolledCoursesSnapshot = await getDocs(
        query(
          collection(db, 'userCourses'),
          where('userId', '==', currentUser.uid)
        )
      );
      const enrolledCourses = enrolledCoursesSnapshot.docs.map(doc => doc.data());
      
      // Calculate total study hours from course durations
      const totalStudyHours = enrolledCourses.reduce((total, course) => {
        const watchedMinutes = course.watchedMinutes || 0;
        return total + (watchedMinutes / 60);
      }, 0);
      
      // Count completed lessons
      const lessonsCompleted = enrolledCourses.reduce((total, course) => {
        return total + (course.completedLessons || 0);
      }, 0);
      
      // Count completed assignments
      const assignmentsSnapshot = await getDocs(
        query(
          collection(db, 'courseSubmissions'),
          where('userId', '==', currentUser.uid),
          where('status', '==', 'submitted')
        )
      );
      const assignmentsDone = assignmentsSnapshot.size;
      
      // Calculate average score from course progress or assignments
      const averageScore = enrolledCourses.length > 0
        ? enrolledCourses.reduce((total, course) => total + (course.progress || 0), 0) / enrolledCourses.length
        : 0;

      // Load learning streak
      const streakManager = new StudyStreakManager(currentUser.uid);
      const streakData = await streakManager.getStreakData();
      
      // Get streak history (last 7 days) - optimized
      // Get recent activities
      let activitiesSnapshot;
      try {
        // Try with orderBy first (if timestamp is indexed)
        activitiesSnapshot = await getDocs(
          query(
            collection(db, 'userActivities'),
            where('userId', '==', currentUser.uid),
            orderBy('timestamp', 'desc'),
            limit(100)
          )
        );
      } catch (error) {
        // If orderBy fails, get without sorting
        activitiesSnapshot = await getDocs(
          query(
            collection(db, 'userActivities'),
            where('userId', '==', currentUser.uid),
            limit(100)
          )
        );
      }
      
      // Create a set of dates with activity from last 7 days
      const activeDates = new Set();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setHours(0, 0, 0, 0);
      
      activitiesSnapshot.docs.forEach(doc => {
        const activity = doc.data();
        let activityDate;
        if (activity.timestamp) {
          activityDate = activity.timestamp.toDate ? 
            activity.timestamp.toDate() : 
            new Date(activity.timestamp);
        } else if (activity.createdAt) {
          activityDate = activity.createdAt.toDate ? 
            activity.createdAt.toDate() : 
            new Date(activity.createdAt);
        } else {
          return; // Skip if no timestamp
        }
        
        // Only include if within last 7 days
        if (activityDate >= sevenDaysAgo) {
          const dateStr = activityDate.toISOString().split('T')[0];
          activeDates.add(dateStr);
        }
      });
      
      // Build streak history for last 7 days
      const streakHistory = [];
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      for (let i = 6; i >= 0; i--) {
        const date = new Date(today);
        date.setDate(date.getDate() - i);
        const dateStr = date.toISOString().split('T')[0];
        
        streakHistory.push({
          date: dateStr,
          active: activeDates.has(dateStr)
        });
      }

      setAnalyticsData({
        studyTimeDistribution,
        performanceMetrics: {
          averageScore: Math.round(averageScore),
          studyHours: Math.round(totalStudyHours),
          lessonsCompleted,
          assignmentsDone
        },
        learningStreak: streakData.currentStreak || 0,
        streakHistory
      });
    } catch (error) {
      // Set defaults on error
      setAnalyticsData({
        studyTimeDistribution: [],
        performanceMetrics: {
          averageScore: 0,
          studyHours: 0,
          lessonsCompleted: 0,
          assignmentsDone: 0
        },
        learningStreak: 0,
        streakHistory: []
      });
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading courses...</p>
        </div>
      </div>
    );
  }

  const getProgressColor = (progress) => {
    if (progress >= 80) return 'text-green-600';
    if (progress >= 60) return 'text-blue-600';
    if (progress >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">Active</span>;
      case 'completed':
        return <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">Completed</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded-full text-xs font-medium">Inactive</span>;
    }
  };


  const joinLiveClass = (liveClass) => {
    // Simulate joining live class
    // Open live class in new window/tab
    const liveClassUrl = `https://meet.google.com/live-class-${liveClass.id}`;
    window.open(liveClassUrl, '_blank');
  };

  // Load lectures for a specific course
  const loadCourseLectures = async (courseId) => {
    try {
      setLoadingLectures(true);
      
      // First try to load from Firebase
      const result = await getLectures(courseId);
      if (result.success && result.data.length > 0) {
        setCourseLectures(result.data);
      } else {
        // Fallback to sample lectures if available in course data
        const course = coursesData.availableCourses.find(c => c.id === courseId);
        if (course && course.lectures) {
          setCourseLectures(course.lectures);
        } else {
          setCourseLectures([]);
        }
      }
    } catch (error) {
      // Fallback to sample lectures if Firebase fails
      const course = coursesData.availableCourses.find(c => c.id === courseId);
      if (course && course.lectures) {
        setCourseLectures(course.lectures);
      } else {
        setCourseLectures([]);
      }
    } finally {
      setLoadingLectures(false);
    }
  };

  const enrollCourse = async (course) => {
    if (!currentUser?.uid) {
      toast.error('Please login to enroll in courses');
      return;
    }

    // Check if course is already enrolled
    const isAlreadyEnrolled = coursesData.enrolledCourses.some(
      enrolledCourse => enrolledCourse.id === course.id
    );

    if (isAlreadyEnrolled) {
      toast.error('You are already enrolled in this course');
      return;
    }

    // Check if course has a price
    const coursePrice = course.price || course.fee || 0;
    
    if (coursePrice > 0) {
      // Course requires payment
      // All courses are admin-created (coachingId = null), so GST applies
      const paymentInfo = {
        type: 'course_enrollment',
        paymentType: 'course_enrollment',
        amount: coursePrice,
        currency: 'INR',
        description: `Enroll in ${course.title}`,
        merchantName: 'Mahstry Private Limited',
        customerName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
        customerEmail: currentUser.email,
        customerPhone: currentUser.phoneNumber || '',
        userId: currentUser.uid,
        enrollmentData: {
          courseId: course.id,
          courseTitle: course.title,
          studentId: currentUser.uid,
          studentName: currentUser.displayName || currentUser.email?.split('@')[0] || 'Student',
          studentEmail: currentUser.email,
          instructorId: course.instructorId || 'unknown-instructor',
          instructorName: course.instructor || 'Unknown Instructor',
          coursePrice: coursePrice
        }
      };

      setPaymentData(paymentInfo);
      setShowPaymentModal(true);
    } else {
      // Free course - enroll directly
      await completeCourseEnrollment(course);
    }
  };

  const completeCourseEnrollment = async (course) => {
    try {
      // Add course to enrolled courses with fallback values
      const newEnrolledCourse = {
        id: course.id || 'unknown-course',
        title: course.title || 'Untitled Course',
        instructor: course.instructor || 'Unknown Instructor',
        instructorAvatar: course.instructorAvatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face',
        thumbnail: course.thumbnail || 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=200&fit=crop',
        progress: 0,
        totalLessons: 20,
        completedLessons: 0,
        duration: course.duration || 120,
        level: course.level || 'beginner',
        rating: course.rating || 4.5,
        students: course.students || 1000,
        status: "active",
        enrolledAt: new Date().toISOString(),
        userId: currentUser.uid,
        userEmail: currentUser.email
      };

      // Filter out undefined values to prevent Firebase errors
      const cleanEnrolledCourse = Object.fromEntries(
        Object.entries(newEnrolledCourse).filter(([key, value]) => value !== undefined)
      );

      // Save to Firebase
      const enrollmentRef = doc(db, 'userCourses', `${currentUser.uid}_${course.id}`);
      await setDoc(enrollmentRef, cleanEnrolledCourse);

      // Update local state
      setCoursesData(prev => ({
        ...prev,
        enrolledCourses: [...prev.enrolledCourses, cleanEnrolledCourse]
      }));

      toast.success('Successfully enrolled in the course!');
    } catch (error) {
      toast.error('Failed to enroll in course');
      throw error; // Re-throw to let calling function handle it
    }
  };

  const handlePaymentSuccess = async (response, transactionData) => {
    try {
      // Complete the course enrollment process
      const enrollmentData = transactionData.enrollmentData;
      
      if (!enrollmentData) {
        throw new Error('Enrollment data not found');
      }
      
      // Create a course object from enrollment data for the enrollment function
      const courseObject = {
        id: enrollmentData.courseId,
        title: enrollmentData.courseTitle,
        instructor: enrollmentData.instructorName || 'Unknown Instructor',
        instructorId: enrollmentData.instructorId || 'unknown-instructor',
        price: enrollmentData.coursePrice
      };
      
      await completeCourseEnrollment(courseObject);
      
      toast.success('Payment successful! You are now enrolled in the course.');
      setShowPaymentModal(false);
    } catch (error) {
      toast.error('Payment successful but failed to enroll. Please contact support.');
    }
  };

  const handlePaymentFailure = (error) => {
    toast.error('Payment failed. Please try again.');
  };

  const viewCertificate = (certificate) => {
    // Open certificate in new window/tab
    const certificateUrl = `https://example.com/certificates/${certificate.id}`;
    window.open(certificateUrl, '_blank');
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      file: file
    }));
    setSubmissionFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setSubmissionFiles(prev => prev.filter(file => file.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const submitAssignment = async (assignment) => {
    if (!userData?.uid) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare submission data
      const submissionData = {
        assignmentId: assignment.id,
        assignmentTitle: assignment.title,
        courseId: assignment.courseId || selectedCourse?.id,
        courseName: assignment.course,
        userId: userData?.uid,
        userName: userData?.displayName || "Current User",
        files: submissionFiles.map(file => ({
          name: file.name,
          size: file.size,
          type: file.type
        })),
        text: submissionText,
        submittedAt: new Date().toISOString(),
        status: 'submitted'
      };

      // Submit to Firebase
      // Reset form
      setSubmissionFiles([]);
      setSubmissionText('');
      setShowAssignmentSubmission(false);
      setSelectedAssignment(null);
      
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSubmissionPortal = (assignment) => {
    setSelectedAssignment(assignment);
    setShowAssignmentSubmission(true);
  };

  const createNewPost = async () => {
    if (!newPostTitle.trim() || !newPostContent.trim()) {
      return;
    }

    if (!currentUser?.uid) {
      return;
    }

    try {
      const newPost = {
        id: Date.now(),
        author: currentUser?.displayName || "Current User",
        authorAvatar: currentUser?.photoURL || "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face",
        title: newPostTitle,
        content: newPostContent,
        date: "Just now",
        replies: 0,
        likes: 0,
        tags: newPostTags.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0),
        userId: currentUser.uid
      };

      // Submit discussion to Firebase
      // Reset form
      setNewPostTitle('');
      setNewPostContent('');
      setNewPostTags('');
      setShowNewPostModal(false);

    } catch (error) {
    }
  };

  const filteredCourses = coursesData.enrolledCourses.filter(course => {
    const matchesFilter = selectedFilter === 'all' || course.status === selectedFilter;
    const matchesSearch = course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.instructor.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  // Function to save course progress updates
  const saveCourseProgress = async (courseId, progressData) => {
    if (!currentUser?.uid) return;

    try {
      const enrollmentRef = doc(db, 'userCourses', `${currentUser.uid}_${courseId}`);
      await setDoc(enrollmentRef, progressData, { merge: true });

      // Update local state
      setCoursesData(prev => ({
        ...prev,
        enrolledCourses: prev.enrolledCourses.map(course => 
          course.id === courseId ? { ...course, ...progressData } : course
        )
      }));

      } catch (error) {
      }
  };

  // Function to load enrolled courses from Firebase
  const loadEnrolledCourses = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      
      // Load available courses first to check if enrolled courses still exist
      const coursesRef = collection(db, 'courses');
      const coursesSnapshot = await getDocs(coursesRef);
      const availableCourses = coursesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const availableCourseIds = new Set(availableCourses.map(course => course.id));
      
      const enrolledCoursesQuery = query(
        collection(db, 'userCourses'),
        where('userId', '==', currentUser.uid),
        orderBy('enrolledAt', 'desc')
      );
      
      const querySnapshot = await getDocs(enrolledCoursesQuery);
      
      // Filter out deleted courses
      const enrolledCourses = querySnapshot.docs
        .map(doc => {
          const data = doc.data();
          // Extract courseId from document ID (format: userId_courseId)
          const docIdParts = doc.id.split('_');
          let courseId = null;
          
          if (docIdParts.length > 1 && docIdParts[0] === currentUser.uid) {
            courseId = docIdParts.slice(1).join('_');
          } else if (data.courseId) {
            courseId = data.courseId;
          } else if (data.id && availableCourseIds.has(data.id)) {
            courseId = data.id;
          }
          
          // Check if this course still exists
          if (courseId && availableCourseIds.has(courseId)) {
            const actualCourse = availableCourses.find(c => c.id === courseId);
            return {
              ...data,
              id: courseId,
              courseId: courseId,
              title: actualCourse?.title || data.title,
              instructor: actualCourse?.instructor || data.instructor,
              instructorAvatar: actualCourse?.instructorAvatar || data.instructorAvatar,
              thumbnail: actualCourse?.thumbnail || data.thumbnail,
              level: actualCourse?.level || data.level,
              rating: actualCourse?.rating || data.rating,
              students: actualCourse?.students || data.students,
              duration: actualCourse?.duration || data.duration
            };
          }
          
          return null; // Course was deleted
        })
        .filter(course => course !== null);
      
      // Update local state
      setCoursesData(prev => ({
        ...prev,
        enrolledCourses: enrolledCourses
      }));
      
    } catch (error) {
      } finally {
      setLoading(false);
    }
  };

  // Function to force reload course data
  const reloadCourseData = async () => {
    if (!currentUser?.uid) return;
    
    try {
      setLoading(true);
      await loadEnrolledCourses();
      
    } catch (error) {
      } finally {
      setLoading(false);
    }
  };



  return (
    <div className={`space-y-4 sm:space-y-6 ${mobileUtils.getContainerClasses()}`}>
      {/* Header */}
      <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
        <div className="px-1">
          <h1 className={`font-bold text-gray-900 dark:text-white ${mobileUtils.getTextClasses('xl')}`}>
            My Courses
          </h1>
          <p className={`text-gray-600 dark:text-gray-400 mt-1 ${mobileUtils.getTextClasses('sm')}`}>
            Continue learning and track your progress
          </p>
        </div>
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
          <button 
            onClick={reloadCourseData}
            className="btn-secondary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5"
            title="Reload course data"
          >
            <RefreshCw size={16} className="mr-2 flex-shrink-0" />
            <span>Reload</span>
          </button>

          <button 
            onClick={() => setShowCatalog(!showCatalog)}
            className="btn-secondary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5"
          >
            <Search size={16} className="mr-2 flex-shrink-0" />
            <span>{showCatalog ? 'Hide Catalog' : 'Browse Courses'}</span>
          </button>
          <button 
            onClick={() => setShowCatalog(true)}
            className="btn-primary inline-flex items-center justify-center min-h-[44px] text-sm px-4 py-2.5"
          >
            <Plus size={16} className="mr-2 flex-shrink-0" />
            <span>Enroll New Course</span>
          </button>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="flex flex-col space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search courses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={(e) => mobileUtils.preventZoom(e.target)}
              className="pl-10 pr-4 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white w-full text-base sm:text-sm min-h-[44px]"
            />
          </div>
          <select 
            value={selectedFilter} 
            onChange={(e) => setSelectedFilter(e.target.value)}
            className="px-3 py-3 sm:py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-base sm:text-sm min-h-[44px]"
          >
            <option value="all">All Courses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
          </select>
        </div>
        
        {/* Action Buttons - Mobile Responsive */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2 sm:gap-3">
          <button 
            onClick={() => setShowCertificates(true)}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-2.5 sm:py-2 inline-flex items-center justify-center min-h-[44px]"
          >
            <Trophy className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Certificates ({coursesData.certificates.length})</span>
          </button>
          <button 
            onClick={() => setShowStudyMaterials(true)}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-2.5 sm:py-2 inline-flex items-center justify-center min-h-[44px]"
          >
            <BookOpenCheck className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Materials</span>
          </button>
          <button 
            onClick={() => navigate('/dashboard/live-classes')}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-2.5 sm:py-2 inline-flex items-center justify-center min-h-[44px]"
          >
            <Mic className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Live Classes</span>
          </button>
          <button 
            onClick={() => setShowAnalytics(true)}
            className="btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-2.5 sm:py-2 inline-flex items-center justify-center min-h-[44px]"
          >
            <TrendingUp className="w-4 h-4 mr-1 sm:mr-2 flex-shrink-0" />
            <span className="truncate">Analytics</span>
          </button>
        </div>
      </div>

      {/* Stats Overview */}
      <div className={mobileUtils.getGridClasses({ mobile: 2, tablet: 3, desktop: 5 })}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-3 sm:p-4"
        >
          <div className="flex items-center justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400 truncate">Enrolled</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">{coursesData.stats.enrolledCourses}</p>
            </div>
            <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0">
              <BookOpen className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
              <p className="text-2xl font-bold text-green-600">{coursesData.stats.completedCourses}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <Award className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Study Hours</p>
              <p className="text-2xl font-bold text-purple-600">
                {coursesData.stats.totalHours % 1 === 0 
                  ? coursesData.stats.totalHours 
                  : coursesData.stats.totalHours.toFixed(1)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <Clock className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Avg Score</p>
              <p className="text-2xl font-bold text-orange-600">{coursesData.stats.averageScore}%</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="card p-4"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Certificates</p>
              <p className="text-2xl font-bold text-indigo-600">{coursesData.stats.certificates}</p>
            </div>
            <div className="p-3 bg-indigo-100 rounded-lg">
              <Target className="w-6 h-6 text-indigo-600" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Course Catalog - Moved to top for better visibility */}
      {showCatalog && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card p-6"
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Available Courses
            </h3>
            <div className="flex items-center space-x-3">
              <select 
                value={selectedCategory} 
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="all">All Categories</option>
                <option value="Quantitative">Quantitative</option>
                <option value="Verbal">Verbal</option>
                <option value="Data Interpretation">Data Interpretation</option>
                <option value="Logical Reasoning">Logical Reasoning</option>
              </select>
              <select 
                value={selectedLevel} 
                onChange={(e) => setSelectedLevel(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white text-sm"
              >
                <option value="all">All Levels</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {coursesData.availableCourses
              .filter(course => 
                (selectedCategory === 'all' || course.category === selectedCategory) &&
                (selectedLevel === 'all' || course.level === selectedLevel)
              )
              .map((course, index) => (
                <motion.div
                  key={course.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="relative mb-4">
                    <img 
                      src={course.thumbnail} 
                      alt={course.title}
                      className="w-full h-32 object-cover rounded-lg"
                    />
                    <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-xs font-medium">
                      {course.discount} OFF
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                      {course.level}
                    </div>
                  </div>
                  
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                    {course.title}
                  </h4>
                  
                  <div className="flex items-center space-x-2 mb-3">
                    <img 
                      src={course.instructorAvatar} 
                      alt={course.instructor}
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {course.instructor}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-1">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-sm font-medium">{course.rating}</span>
                      <span className="text-sm text-gray-500">({course.students})</span>
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      {course.duration}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-lg font-bold text-gray-900 dark:text-white">
                        {course.price || course.fee ? `₹${course.price || course.fee}` : 'Free'}
                      </span>
                      {course.originalPrice && (
                        <span className="text-sm text-gray-500 line-through ml-2">
                          ₹{course.originalPrice}
                        </span>
                      )}
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-400">
                      Starts {course.startDate}
                    </span>
                  </div>
                  
                  <div className="flex space-x-2">
                    {course.lectures && course.lectures.length > 0 && course.lectures[0].type === 'video' && (
                      <button 
                        onClick={() => playLecture(course.lectures[0])}
                        className="flex-1 btn-secondary flex items-center justify-center space-x-1"
                      >
                        <Play className="w-4 h-4" />
                        <span>Watch Sample</span>
                      </button>
                    )}
                    <button 
                      onClick={() => enrollCourse(course)}
                      className="flex-1 btn-primary flex items-center justify-center space-x-1"
                    >
                      <CreditCard className="w-4 h-4" />
                      <span>{course.price || course.fee ? 'Pay & Enroll' : 'Enroll Now'}</span>
                    </button>
                  </div>
                </motion.div>
              ))}
          </div>
        </motion.div>
      )}

      {/* Enrolled Courses */}
      <div className="card p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Continue Learning
          </h3>
                     <button 
             onClick={() => setShowCatalog(true)}
             className="text-primary-600 hover:text-primary-700 text-sm font-medium"
           >
             View All Courses
           </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCourses.map((course, index) => (
            <motion.div
              key={course.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-lg transition-shadow cursor-pointer"
            >
              <div className="relative mb-4">
                <img 
                  src={course.thumbnail} 
                  alt={course.title}
                  className="w-full h-32 object-cover rounded-lg"
                />
                <div className="absolute top-2 right-2">
                  {getStatusBadge(course.status)}
                </div>
                <div className="absolute bottom-2 left-2 bg-black bg-opacity-75 text-white px-2 py-1 rounded text-xs">
                  {course.level}
                </div>
              </div>
              
              <h4 className="font-semibold text-gray-900 dark:text-white mb-2 line-clamp-2">
                {course.title}
              </h4>
              
              <div className="flex items-center space-x-2 mb-3">
                <img 
                  src={course.instructorAvatar} 
                  alt={course.instructor}
                  className="w-6 h-6 rounded-full"
                />
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {course.instructor}
                </span>
              </div>
              
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600 dark:text-gray-400">Progress</span>
                  <span className={`font-medium ${getProgressColor(course.progress)}`}>
                    {course.progress}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      course.progress >= 80 ? 'bg-green-500' :
                      course.progress >= 60 ? 'bg-blue-500' :
                      course.progress >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${course.progress}%` }}
                  ></div>
                </div>
              </div>
              
              <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400 mb-4">
                <span>{course.completedLessons}/{course.totalLessons} lessons</span>
                <span>{course.duration}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                  <span className="text-sm font-medium">{course.rating}</span>
                  <span className="text-sm text-gray-500">({course.students})</span>
                </div>
                <div className="flex space-x-2">
                  {course.lectures && course.lectures.length > 0 && course.lectures[0].type === 'video' && (
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        playLecture(course.lectures[0]);
                      }}
                      className="btn-secondary text-xs px-2 py-1 flex items-center space-x-1"
                    >
                      <Play className="w-3 h-3" />
                      <span>Watch</span>
                    </button>
                  )}
                  <button 
                    onClick={() => {
                      if (course.status === 'completed') {
                        // Find the certificate for this course
                        const certificate = coursesData.certificates.find(cert => cert.courseId === course.id);
                        if (certificate) {
                          viewCertificate(certificate);
                        } else {
                        }
                      } else {
                        setSelectedCourse(course);
                        setCurrentLesson(0); // Reset to first lesson
                        setShowCourseDetails(true);
                        // Load lectures for this course
                        loadCourseLectures(course.id);
                      }
                    }}
                    className="btn-primary text-sm px-4 py-2"
                  >
                    {course.status === 'completed' ? 'View Certificate' : 'Continue'}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>




       {/* Course Details Modal */}
       {showCourseDetails && selectedCourse && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4">
           <motion.div
             initial={{ scale: 0.9, opacity: 0 }}
             animate={{ scale: 1, opacity: 1 }}
             className="bg-white dark:bg-gray-800 rounded-none sm:rounded-lg w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-6xl overflow-y-auto"
           >
             <div className="p-3 sm:p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white truncate pr-2">
                   {selectedCourse.title}
                 </h2>
                 <button 
                   onClick={() => setShowCourseDetails(false)}
                   className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
                 >
                   <X className="w-4 h-4" />
                 </button>
               </div>
             </div>
             
             <div className="p-3 sm:p-6">
                {/* Course Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4 sm:mb-6 overflow-x-auto scrollbar-hide -mx-3 sm:mx-0 px-3 sm:px-0">
                  <button
                    onClick={() => setCourseDetailTab('content')}
                    className={`px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      courseDetailTab === 'content'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Course Content
                  </button>
                  <button
                    onClick={() => setCourseDetailTab('discussions')}
                    className={`px-2 sm:px-4 py-2 text-xs sm:text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex-shrink-0 ${
                      courseDetailTab === 'discussions'
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    Discussions ({courseDiscussions[selectedCourse?.id]?.length || 0})
                  </button>
                </div>

                {courseDetailTab === 'content' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
                    {/* Video Player */}
                    <div className="lg:col-span-2">
                   <div className="bg-black rounded-lg aspect-video mb-3 sm:mb-4">
                     {courseLectures.length > 0 && currentLesson < courseLectures.length && courseLectures[currentLesson].type === 'video' && courseLectures[currentLesson].videoUrl ? (
                       (() => {
                         const videoUrl = courseLectures[currentLesson].videoUrl;
                         const embedUrl = convertToEmbedUrl(videoUrl);
                         
                         if (embedUrl && isValidVideoUrl(videoUrl)) {
                           return (
                             <iframe
                               src={embedUrl}
                               title={courseLectures[currentLesson].title}
                               className="w-full h-full rounded-lg"
                               frameBorder="0"
                               allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                               allowFullScreen
                             />
                           );
                         } else {
                           return (
                             <div className="w-full h-full flex items-center justify-center text-white">
                               <div className="text-center px-2">
                                 <Play className="w-10 h-10 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 opacity-50" />
                                 <p className="text-sm sm:text-lg font-medium">Lecture Content</p>
                                 <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2 line-clamp-2">
                                   {courseLectures[currentLesson].title}
                                 </p>
                                 <button 
                                   onClick={() => playLecture(courseLectures[currentLesson])}
                                   className="mt-2 sm:mt-4 px-4 sm:px-6 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm rounded-lg transition-colors"
                                 >
                                   Open in Video Player
                                 </button>
                               </div>
                             </div>
                           );
                         }
                       })()
                     ) : (
                       <div className="w-full h-full flex items-center justify-center text-white">
                         <div className="text-center px-2">
                           <Play className="w-10 h-10 sm:w-16 sm:h-16 mx-auto mb-2 sm:mb-4 opacity-50" />
                           <p className="text-sm sm:text-lg font-medium">Lecture Content</p>
                           <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2 line-clamp-2">
                             {courseLectures.length > 0 && currentLesson < courseLectures.length 
                               ? courseLectures[currentLesson].title 
                               : `Lesson ${currentLesson + 1}: Introduction to ${selectedCourse.title}`}
                           </p>
                           {courseLectures.length > 0 && currentLesson < courseLectures.length && courseLectures[currentLesson] && (
                             <button 
                               onClick={() => playLecture(courseLectures[currentLesson])}
                               className="mt-2 sm:mt-4 px-4 sm:px-6 py-1.5 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm rounded-lg transition-colors"
                             >
                               Open in Video Player
                             </button>
                           )}
                         </div>
                       </div>
                     )}
                   </div>
                   
                   <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 mb-3 sm:mb-4">
                     <h3 className="text-sm sm:text-lg font-semibold text-gray-900 dark:text-white break-words flex-1">
                       {courseLectures.length > 0 && currentLesson < courseLectures.length 
                         ? courseLectures[currentLesson].title 
                         : `Lesson ${currentLesson + 1}: Introduction to ${selectedCourse.title}`}
                     </h3>
                     <div className="flex items-center space-x-2 flex-shrink-0">
                       <button 
                         onClick={() => setCurrentLesson(Math.max(0, currentLesson - 1))}
                         disabled={currentLesson === 0}
                         className={`btn-secondary text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-1 ${currentLesson === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                       >
                         Previous
                       </button>
                       <button 
                         onClick={async () => {
                           const totalLectures = courseLectures.length || selectedCourse.totalLessons;
                           const nextLesson = Math.min(totalLectures - 1, currentLesson + 1);
                           setCurrentLesson(nextLesson);
                           
                           // Update progress if moving to next lesson (only for non-video lessons or completed videos)
                           const nextLecture = courseLectures[nextLesson];
                           if (nextLesson > currentLesson && nextLesson > selectedCourse.completedLessons && 
                               (nextLecture.type !== 'video' || completedVideos.has(nextLecture.id))) {
                             const newCompletedLessons = Math.min(Math.max(selectedCourse.completedLessons, nextLesson), totalLectures);
                             const newProgress = Math.min(100, Math.round((newCompletedLessons / totalLectures) * 100));
                             
                             await saveCourseProgress(selectedCourse.id, {
                               completedLessons: newCompletedLessons,
                               progress: newProgress
                             });
                           }
                         }}
                         disabled={currentLesson === (courseLectures.length || selectedCourse.totalLessons) - 1}
                         className={`btn-primary text-xs sm:text-sm px-2 sm:px-3 py-1.5 sm:py-1 ${currentLesson === (courseLectures.length || selectedCourse.totalLessons) - 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                       >
                         Next
                       </button>
                     </div>
                   </div>
                   
                   <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 sm:p-4">
                     <h4 className="font-medium text-sm sm:text-base text-gray-900 dark:text-white mb-1.5 sm:mb-2">
                       Lesson Description
                     </h4>
                     <p className="text-gray-600 dark:text-gray-400 text-xs sm:text-sm leading-relaxed">
                       {courseLectures.length > 0 && currentLesson < courseLectures.length 
                         ? courseLectures[currentLesson].description || 'No description available for this lecture.'
                         : `This lesson covers the fundamental concepts and techniques needed to master ${selectedCourse.title.toLowerCase()}. You'll learn step-by-step approaches and practice with real examples.`}
                     </p>
                   </div>
                 </div>
                 
                 {/* Enhanced Course Content */}
                 <div className="lg:col-span-1">
                   <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-3 sm:p-6 shadow-sm">
                     <div className="flex items-center justify-between mb-4 sm:mb-6">
                       <h4 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white">
                         Course Content
                       </h4>
                       <div className="flex items-center space-x-1.5 sm:space-x-2">
                         <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                           {courseLectures.length} lessons
                         </span>
                         <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-green-500 rounded-full"></div>
                       </div>
                     </div>
                     
                     <div className="space-y-2 sm:space-y-3">
                       {loadingLectures ? (
                         <div className="text-center py-6 sm:py-8">
                           <div className="relative">
                             <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-4 border-blue-200 border-opacity-20 mx-auto mb-3 sm:mb-4"></div>
                             <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-4 border-transparent border-t-blue-600 absolute top-0 left-1/2 transform -translate-x-1/2"></div>
                           </div>
                           <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">Loading lectures...</p>
                         </div>
                       ) : courseLectures.length > 0 ? (
                         courseLectures.map((lecture, i) => (
                           <motion.div 
                             key={lecture.id}
                             initial={{ opacity: 0, x: -20 }}
                             animate={{ opacity: 1, x: 0 }}
                             transition={{ delay: i * 0.1 }}
                             className={`group relative p-2.5 sm:p-4 rounded-xl cursor-pointer transition-all duration-200 ${
                               i === currentLesson 
                                 ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg transform scale-105' 
                                 : 'bg-white dark:bg-gray-800 hover:bg-blue-50 dark:hover:bg-gray-700 hover:shadow-md'
                             }`}
                             onClick={async () => {
                               setCurrentLesson(i);
                               
                               // If it's a video lecture, open the video player
                               if (lecture.type === 'video' && lecture.videoUrl) {
                                 playLecture(lecture);
                               }
                               
                               // Only update progress if this is a non-video lesson or if the video was already completed
                               if (lecture.type !== 'video' || completedVideos.has(lecture.id)) {
                                 if (i > selectedCourse.completedLessons) {
                                   const newCompletedLessons = Math.min(Math.max(selectedCourse.completedLessons, i), courseLectures.length);
                                   const newProgress = Math.min(100, Math.round((newCompletedLessons / courseLectures.length) * 100));
                                   
                                   await saveCourseProgress(selectedCourse.id, {
                                     completedLessons: newCompletedLessons,
                                     progress: newProgress
                                   });
                                 }
                               }
                             }}
                           >
                             <div className="flex items-center space-x-2 sm:space-x-4">
                               <div className={`relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-bold transition-all duration-200 flex-shrink-0 ${
                                 (i < selectedCourse.completedLessons && (courseLectures[i].type !== 'video' || completedVideos.has(courseLectures[i].id))) 
                                   ? 'bg-green-500 text-white shadow-lg' 
                                   : i === currentLesson 
                                     ? 'bg-white text-blue-600' 
                                     : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400 group-hover:bg-blue-100 dark:group-hover:bg-gray-500'
                               }`}>
                                 {(i < selectedCourse.completedLessons && (courseLectures[i].type !== 'video' || completedVideos.has(courseLectures[i].id))) ? (
                                   <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                 ) : (
                                   i + 1
                                 )}
                                 {i === currentLesson && (
                                   <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 bg-blue-600 rounded-full animate-pulse"></div>
                                 )}
                               </div>
                               
                               <div className="flex-1 min-w-0">
                                 <h5 className={`text-xs sm:text-sm font-semibold truncate ${
                                   i === currentLesson ? 'text-white' : 'text-gray-900 dark:text-white'
                                 }`}>
                                   {lecture.title}
                                 </h5>
                                 {lecture.type === 'video' && (
                                   <div className="flex items-center space-x-1.5 sm:space-x-2 mt-0.5 sm:mt-1">
                                     <Video className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${
                                       i === currentLesson ? 'text-white' : 'text-blue-500'
                                     }`} />
                                     <span className={`text-[10px] sm:text-xs ${
                                       i === currentLesson ? 'text-white text-opacity-80' : 'text-gray-500'
                                     }`}>
                                       Video Lecture
                                     </span>
                                   </div>
                                 )}
                               </div>
                               
                               <div className="flex items-center space-x-1.5 sm:space-x-3 flex-shrink-0">
                                 <span className={`text-[10px] sm:text-xs font-medium ${
                                   i === currentLesson ? 'text-white text-opacity-80' : 'text-gray-500'
                                 }`}>
                                   {lecture.duration ? `${lecture.duration} min` : 'N/A'}
                                 </span>
                                 {lecture.type === 'video' && lecture.videoUrl && (
                                   <motion.button
                                     onClick={(e) => {
                                       e.stopPropagation();
                                       playLecture(lecture);
                                     }}
                                     className={`p-1.5 sm:p-2 rounded-full transition-all duration-200 ${
                                       i === currentLesson 
                                         ? 'bg-white bg-opacity-20 text-white hover:bg-opacity-30' 
                                         : 'bg-blue-500 text-white hover:bg-blue-600 shadow-md'
                                     }`}
                                     title="Play Video"
                                     whileHover={{ scale: 1.1 }}
                                     whileTap={{ scale: 0.95 }}
                                   >
                                     <Play className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                   </motion.button>
                                 )}
                               </div>
                             </div>
                             
                             {/* Progress indicator */}
                             {i < selectedCourse.completedLessons && (courseLectures[i].type !== 'video' || completedVideos.has(courseLectures[i].id)) && (
                               <div className="absolute bottom-0 left-0 right-0 h-1 bg-green-500 rounded-b-xl"></div>
                             )}
                           </motion.div>
                         ))
                       ) : (
                         <motion.div 
                           initial={{ opacity: 0, scale: 0.95 }}
                           animate={{ opacity: 1, scale: 1 }}
                           className="text-center py-6 sm:py-8 bg-white dark:bg-gray-800 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600"
                         >
                           <BookOpen className="w-8 h-8 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                           <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">No lectures available yet</p>
                         </motion.div>
                       )}
                     </div>
                   </div>
                   
                   <div className="mt-4 sm:mt-6 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-3 sm:p-6 shadow-sm">
                     <div className="flex items-center justify-between mb-4 sm:mb-6">
                       <h4 className="text-base sm:text-xl font-bold text-gray-900 dark:text-white">
                         Course Progress
                       </h4>
                       <div className="flex items-center space-x-1.5 sm:space-x-2">
                         <Trophy className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-500" />
                         <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 font-medium">
                           {selectedCourse.progress >= 100 ? 'Completed!' : 'In Progress'}
                         </span>
                       </div>
                     </div>
                     
                     {/* Enhanced Progress Bar */}
                     <div className="mb-4 sm:mb-6">
                       <div className="flex justify-between items-center mb-2 sm:mb-3">
                         <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">Overall Progress</span>
                         <span className={`text-base sm:text-lg font-bold ${
                           selectedCourse.progress >= 80 ? 'text-green-600 dark:text-green-400' :
                           selectedCourse.progress >= 60 ? 'text-blue-600 dark:text-blue-400' :
                           selectedCourse.progress >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 
                           'text-red-600 dark:text-red-400'
                         }`}>
                           {selectedCourse.progress}%
                         </span>
                       </div>
                       <div className="relative">
                         <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3 overflow-hidden">
                           <motion.div 
                             className={`h-3 rounded-full ${
                               selectedCourse.progress >= 80 ? 'bg-gradient-to-r from-green-500 to-emerald-500' :
                               selectedCourse.progress >= 60 ? 'bg-gradient-to-r from-blue-500 to-indigo-500' :
                               selectedCourse.progress >= 40 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 
                               'bg-gradient-to-r from-red-500 to-pink-500'
                             }`}
                             initial={{ width: 0 }}
                             animate={{ width: `${selectedCourse.progress}%` }}
                             transition={{ duration: 1, ease: "easeOut" }}
                           />
                         </div>
                         {selectedCourse.progress >= 100 && (
                           <div className="absolute -top-2 -right-2 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                             <CheckCircle className="w-4 h-4 text-white" />
                           </div>
                         )}
                       </div>
                     </div>
                     
                     {/* Enhanced Stats Grid */}
                     <div className="grid grid-cols-1 gap-4">
                       <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600">
                         <div className="flex items-center justify-between">
                           <div className="flex items-center space-x-3">
                             <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                               <BookOpenCheck className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                             </div>
                             <div>
                               <p className="text-sm text-gray-600 dark:text-gray-400">Lessons Completed</p>
                               <p className="text-lg font-bold text-gray-900 dark:text-white">
                                 {(() => {
                                   const actualCompleted = courseLectures.filter((lecture, index) => 
                                     index < selectedCourse.completedLessons && 
                                     (lecture.type !== 'video' || completedVideos.has(lecture.id))
                                   ).length;
                                   return `${actualCompleted}/${courseLectures.length || selectedCourse.totalLessons}`;
                                 })()}
                               </p>
                             </div>
                           </div>
                           <div className="text-right">
                             <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                               {(() => {
                                 const actualCompleted = courseLectures.filter((lecture, index) => 
                                   index < selectedCourse.completedLessons && 
                                   (lecture.type !== 'video' || completedVideos.has(lecture.id))
                                 ).length;
                                 const total = courseLectures.length || selectedCourse.totalLessons;
                                 return Math.min(100, Math.round((actualCompleted / Math.max(1, total)) * 100));
                               })()}%
                             </div>
                           </div>
                         </div>
                       </div>
                       
                       <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600">
                         <div className="flex items-center justify-between">
                           <div className="flex items-center space-x-3">
                             <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900/30 rounded-full flex items-center justify-center">
                               <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                             </div>
                             <div>
                               <p className="text-sm text-gray-600 dark:text-gray-400">Time Spent</p>
                               <p className="text-lg font-bold text-gray-900 dark:text-white">
                                 {Math.round(selectedCourse.completedLessons * 20)} min
                               </p>
                             </div>
                           </div>
                           <div className="text-right">
                             <div className="text-sm text-gray-500 dark:text-gray-400">
                               ~{Math.round(selectedCourse.completedLessons * 20 / 60)} hours
                             </div>
                           </div>
                         </div>
                       </div>
                       
                       <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-100 dark:border-gray-600">
                         <div className="flex items-center justify-between">
                           <div className="flex items-center space-x-3">
                             <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                               <Target className="w-5 h-5 text-green-600 dark:text-green-400" />
                             </div>
                             <div>
                               <p className="text-sm text-gray-600 dark:text-gray-400">Current Streak</p>
                               <p className="text-lg font-bold text-gray-900 dark:text-white">
                                 {Math.min(selectedCourse.completedLessons, 7)} days
                               </p>
                             </div>
                           </div>
                           <div className="text-right">
                             <div className="text-sm text-green-600 dark:text-green-400 font-medium">
                               🔥 Active
                             </div>
                           </div>
                         </div>
                       </div>
                     </div>
                     
                     {/* Achievement Badge */}
                     {selectedCourse.progress >= 100 && (
                       <motion.div 
                         initial={{ opacity: 0, y: 20 }}
                         animate={{ opacity: 1, y: 0 }}
                         className="mt-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-lg p-4 text-center"
                       >
                         <Trophy className="w-8 h-8 text-white mx-auto mb-2" />
                         <h5 className="text-white font-bold text-lg">Course Completed!</h5>
                         <p className="text-white text-opacity-90 text-sm">Congratulations on finishing this course!</p>
                       </motion.div>
                     )}
                   </div>
                 </div>
               </div>
                )}

                {courseDetailTab === 'discussions' && (
                  <div className="space-y-6">
                    {/* Enhanced Discussion Header */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-lg p-6">
                      <div className="flex items-center justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Course Discussions
                          </h3>
                          <p className="text-gray-600 dark:text-gray-300">
                            Connect with fellow learners and share insights about this course
                          </p>
                        </div>
                        <button 
                          onClick={() => setShowNewPostModal(true)}
                          className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 inline-flex items-center"
                        >
                          <Plus className="w-5 h-5 mr-2" />
                          New Discussion
                        </button>
                      </div>
                      
                      {/* Discussion Stats */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                            {courseDiscussions[selectedCourse?.id]?.length || 0}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">Total Posts</div>
                        </div>
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                            {courseDiscussions[selectedCourse?.id]?.reduce((total, post) => total + (post.replies || 0), 0) || 0}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">Total Replies</div>
                        </div>
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 text-center">
                          <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                            {courseDiscussions[selectedCourse?.id]?.reduce((total, post) => total + (post.likes || 0), 0) || 0}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300">Total Likes</div>
                        </div>
                      </div>
                    </div>

                    {/* Discussion Posts with Enhanced Design */}
                    <div className="space-y-6">
                      {courseDiscussions[selectedCourse?.id]?.map((post, index) => (
                        <motion.div 
                          key={post.id} 
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200"
                        >
                          <div className="flex items-start space-x-4 mb-4">
                            <div className="relative">
                              <img 
                                src={post.authorAvatar || 'https://via.placeholder.com/40'} 
                                alt={post.author}
                                className="w-12 h-12 rounded-full border-2 border-blue-200 dark:border-blue-700"
                              />
                              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-800"></div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-3">
                                <div>
                                  <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                    {post.title}
                                  </h3>
                                  <div className="flex items-center space-x-3 text-sm">
                                    <span className="text-blue-600 dark:text-blue-400 font-medium">
                                      {post.author}
                                    </span>
                                    <span className="text-gray-500">•</span>
                                    <span className="text-gray-500">{formatTimestamp(post.date, { includeTime: false, short: true })}</span>
                                    {post.isPinned && (
                                      <>
                                        <span className="text-gray-500">•</span>
                                        <span className="text-yellow-600 dark:text-yellow-400 font-medium flex items-center">
                                          <Star className="w-3 h-3 mr-1" />
                                          Pinned
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {post.isResolved && (
                                    <span className="px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full text-xs font-medium">
                                      Resolved
                                    </span>
                                  )}
                                  <button className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                                    <MoreVertical className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                              
                              <div className="prose prose-sm max-w-none text-gray-700 dark:text-gray-300 mb-4">
                                <p className="leading-relaxed">{post.content}</p>
                              </div>
                              
                              {/* Enhanced Action Buttons */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-6">
                                  <button className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                                    <MessageSquare className="w-4 h-4" />
                                    <span className="font-medium">{post.replies || 0} replies</span>
                                  </button>
                                  <button className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                                    <Heart className="w-4 h-4" />
                                    <span className="font-medium">{post.likes || 0} likes</span>
                                  </button>
                                  <button className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 transition-colors">
                                    <Share2 className="w-4 h-4" />
                                    <span className="font-medium">Share</span>
                                  </button>
                                </div>
                                <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors">
                                  Reply
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {/* Enhanced Tags */}
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
                              {post.tags.map((tag, index) => (
                                <span key={index} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium border border-blue-200 dark:border-blue-700">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </motion.div>
                      ))}

                      {/* Enhanced Empty State */}
                      {(!courseDiscussions[selectedCourse?.id] || courseDiscussions[selectedCourse?.id].length === 0) && (
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600"
                        >
                          <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
                            <MessageSquare className="w-10 h-10 text-blue-600 dark:text-blue-400" />
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            No discussions yet
                          </h3>
                          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
                            Be the first to start a meaningful discussion! Share your thoughts, ask questions, or help other learners.
                          </p>
                          <button 
                            onClick={() => setShowNewPostModal(true)}
                            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-3 rounded-lg font-medium transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 inline-flex items-center"
                          >
                            <Plus className="w-5 h-5 mr-2" />
                            Start First Discussion
                          </button>
                        </motion.div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}

       {/* Assignments Modal */}
       {showAssignments && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <motion.div
             initial={{ scale: 0.9, opacity: 0 }}
             animate={{ scale: 1, opacity: 1 }}
             className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
           >
             <div className="p-6 border-b border-gray-200 dark:border-gray-700">
               <div className="flex items-center justify-between">
                 <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                   Assignments
                 </h2>
                 <button 
                   onClick={() => setShowAssignments(false)}
                   className="text-gray-400 hover:text-gray-600"
                 >
                   <X className="w-4 h-4" />
                 </button>
               </div>
             </div>
             
             <div className="p-6">
               <div className="space-y-4">
                 {coursesData.assignments.map((assignment) => (
                   <div key={assignment.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                     <div className="flex items-start justify-between mb-4">
                       <div className="flex-1">
                         <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                           {assignment.title}
                         </h3>
                         <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                           {assignment.course}
                         </p>
                         <p className="text-sm text-gray-700 dark:text-gray-300">
                           {assignment.description}
                         </p>
                       </div>
                       <div className="text-right ml-4">
                         <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                           assignment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                           assignment.status === 'submitted' ? 'bg-blue-100 text-blue-800' :
                           'bg-green-100 text-green-800'
                         }`}>
                           {assignment.status}
                         </span>
                         {assignment.submittedPoints && (
                           <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                             {assignment.submittedPoints}/{assignment.totalPoints} points
                           </p>
                         )}
                       </div>
                     </div>
                     
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                       <div>
                         <p className="text-sm font-medium text-gray-900 dark:text-white">Due Date</p>
                         <p className="text-sm text-gray-600 dark:text-gray-400">{assignment.dueDate}</p>
                       </div>
                       <div>
                         <p className="text-sm font-medium text-gray-900 dark:text-white">Total Points</p>
                         <p className="text-sm text-gray-600 dark:text-gray-400">{assignment.totalPoints}</p>
                       </div>
                     </div>
                     
                     <div className="mb-4">
                       <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">Attachments</p>
                       <div className="flex flex-wrap gap-2">
                         {assignment.attachments.map((file, index) => (
                           <div key={index} className="flex items-center space-x-2 bg-gray-100 dark:bg-gray-700 px-3 py-2 rounded-lg">
                             <FileText className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                             <span className="text-sm text-gray-700 dark:text-gray-300">{file.name}</span>
                             <span className="text-xs text-gray-500">({file.size})</span>
                           </div>
                         ))}
                       </div>
                     </div>
                     
                     <div className="flex items-center justify-between">
                       <div className="flex items-center space-x-2">
                         {assignment.submitted && (
                           <span className="text-sm text-gray-600 dark:text-gray-400">
                             Submitted: {assignment.submissionDate}
                           </span>
                         )}
                       </div>
                       <div className="flex items-center space-x-2">
                         <button className="btn-secondary text-sm px-4 py-2 inline-flex items-center">
                           <Eye className="w-4 h-4 mr-2" />
                           View Details
                         </button>
                                                   {!assignment.submitted && (
                            <button 
                              onClick={() => openSubmissionPortal(assignment)}
                              className="btn-primary text-sm px-4 py-2 inline-flex items-center"
                            >
                              <Upload className="w-4 h-4 mr-2" />
                              Submit Assignment
                            </button>
                          )}
                       </div>
                     </div>
                   </div>
                 ))}
               </div>
             </div>
           </motion.div>
         </div>
       )}

                   {/* Study Materials Modal */}
          {showStudyMaterials && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                      Study Materials
                    </h2>
                    <button 
                      onClick={() => setShowStudyMaterials(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="space-y-4">
                    {coursesData.studyMaterials.map((material) => (
                      <div key={material.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`p-2 rounded-lg ${
                                material.type === 'pdf' ? 'bg-red-100' : 'bg-blue-100'
                              }`}>
                                {material.type === 'pdf' ? (
                                  <FileText className={`w-5 h-5 ${
                                    material.type === 'pdf' ? 'text-red-600' : 'text-blue-600'
                                  }`} />
                                ) : (
                                  <Video className="w-5 h-5 text-blue-600" />
                                )}
                              </div>
                              <div>
                                <h3 className="font-semibold text-gray-900 dark:text-white">
                                  {material.title}
                                </h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  {material.course}
                                </p>
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 dark:text-gray-300 mb-3">
                              {material.description}
                            </p>
                            <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                              <span className="flex items-center">
                                <FileText className="w-4 h-4 mr-1" />
                                {material.views} views
                              </span>
                              <span className="flex items-center">
                                <Star className="w-4 h-4 mr-1 text-yellow-500 fill-current" />
                                {material.rating}
                              </span>
                              <span>{material.size}</span>
                            </div>
                          </div>

                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>
          )}

          {/* Certificates Modal */}
          {showCertificates && (
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
             <motion.div
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
             >
               <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                 <div className="flex items-center justify-between">
                   <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                     My Certificates
                   </h2>
                   <button 
                     onClick={() => setShowCertificates(false)}
                     className="text-gray-400 hover:text-gray-600"
                   >
                     <X className="w-4 h-4" />
                   </button>
                 </div>
               </div>
               
               <div className="p-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   {coursesData.certificates.map((certificate) => (
                     <div key={certificate.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-6">
                       <div className="flex items-center justify-between mb-4">
                         <div className="flex items-center space-x-3">
                           <div className="p-3 bg-yellow-100 rounded-lg">
                             <Trophy className="w-8 h-8 text-yellow-600" />
                           </div>
                           <div>
                             <h3 className="font-semibold text-gray-900 dark:text-white">
                               {certificate.title}
                             </h3>
                             <p className="text-sm text-gray-600 dark:text-gray-400">
                               {certificate.instructor}
                             </p>
                           </div>
                         </div>
                         <div className="text-right">
                           <div className="text-2xl font-bold text-green-600">
                             {certificate.grade}
                           </div>
                           <div className="text-sm text-gray-600 dark:text-gray-400">
                             {certificate.score}
                           </div>
                         </div>
                       </div>
                       
                       <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                         <div>
                           <p className="text-gray-600 dark:text-gray-400">Issue Date</p>
                           <p className="font-medium text-gray-900 dark:text-white">
                             {formatTimestamp(certificate.issueDate, { includeTime: false, short: true })}
                           </p>
                         </div>
                         <div>
                           <p className="text-gray-600 dark:text-gray-400">Course ID</p>
                           <p className="font-medium text-gray-900 dark:text-white">
                             #{certificate.courseId}
                           </p>
                         </div>
                       </div>
                       
                                               <div className="flex items-center justify-between">
                          <button 
                            onClick={() => viewCertificate(certificate)}
                            className="btn-secondary text-sm px-4 py-2 inline-flex items-center"
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Certificate
                          </button>
                         <button 
                           onClick={() => viewCertificate(certificate)}
                           className="btn-primary text-sm px-4 py-2 inline-flex items-center"
                         >
                           <Eye className="w-4 h-4 mr-2" />
                           View PDF
                         </button>
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             </motion.div>
           </div>
         )}

         {/* Analytics Modal */}
         {showAnalytics && (
           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
             <motion.div
               initial={{ scale: 0.9, opacity: 0 }}
               animate={{ scale: 1, opacity: 1 }}
               className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
             >
               <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                 <div className="flex items-center justify-between">
                   <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                     Course Analytics
                   </h2>
                   <button 
                     onClick={() => setShowAnalytics(false)}
                     className="text-gray-400 hover:text-gray-600"
                   >
                     <X className="w-4 h-4" />
                   </button>
                 </div>
               </div>
               
               <div className="p-6">
                 <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                   {/* Learning Progress */}
                   <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                       Learning Progress
                     </h3>
                     <div className="space-y-4">
                       {coursesData.enrolledCourses.map((course) => (
                         <div key={course.id} className="space-y-2">
                           <div className="flex justify-between text-sm">
                             <span className="text-gray-600 dark:text-gray-400">{course.title}</span>
                             <span className="font-medium">{course.progress}%</span>
                           </div>
                           <div className="w-full bg-gray-200 rounded-full h-2">
                             <div 
                               className={`h-2 rounded-full ${
                                 course.progress >= 80 ? 'bg-green-500' :
                                 course.progress >= 60 ? 'bg-blue-500' :
                                 course.progress >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                               }`}
                               style={{ width: `${course.progress}%` }}
                             ></div>
                           </div>
                         </div>
                       ))}
                     </div>
                   </div>

                   {/* Study Time Distribution */}
                   <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                       Study Time Distribution
                     </h3>
                     <div className="space-y-3">
                       {analyticsData.studyTimeDistribution.length > 0 ? (
                         analyticsData.studyTimeDistribution.map((item, index) => (
                           <div key={index} className="flex justify-between items-center">
                             <span className="text-sm text-gray-600 dark:text-gray-400">{item.category}</span>
                             <span className="text-sm font-medium">{item.hours} hours</span>
                           </div>
                         ))
                       ) : (
                         <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                           No study time data available yet
                         </p>
                       )}
                     </div>
                   </div>

                   {/* Performance Metrics */}
                   <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                       Performance Metrics
                     </h3>
                     <div className="grid grid-cols-2 gap-4">
                       <div className="text-center">
                         <div className="text-2xl font-bold text-green-600">{analyticsData.performanceMetrics.averageScore}%</div>
                         <div className="text-sm text-gray-600 dark:text-gray-400">Average Score</div>
                       </div>
                       <div className="text-center">
                         <div className="text-2xl font-bold text-blue-600">{analyticsData.performanceMetrics.studyHours}</div>
                         <div className="text-sm text-gray-600 dark:text-gray-400">Study Hours</div>
                       </div>
                       <div className="text-center">
                         <div className="text-2xl font-bold text-purple-600">{analyticsData.performanceMetrics.lessonsCompleted}</div>
                         <div className="text-sm text-gray-600 dark:text-gray-400">Lessons Completed</div>
                       </div>
                       <div className="text-center">
                         <div className="text-2xl font-bold text-orange-600">{analyticsData.performanceMetrics.assignmentsDone}</div>
                         <div className="text-sm text-gray-600 dark:text-gray-400">Assignments Done</div>
                       </div>
                     </div>
                   </div>

                   {/* Learning Streak */}
                   <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                       Learning Streak
                     </h3>
                     <div className="text-center">
                       <div className="text-3xl font-bold text-green-600 mb-2">{analyticsData.learningStreak} {analyticsData.learningStreak === 1 ? 'day' : 'days'}</div>
                       <div className="text-sm text-gray-600 dark:text-gray-400 mb-4">Current Streak</div>
                       <div className="flex justify-center space-x-1">
                         {analyticsData.streakHistory.length > 0 ? (
                           analyticsData.streakHistory.map((day, i) => (
                             <div 
                               key={i} 
                               className={`w-3 h-3 rounded-full ${day.active ? 'bg-green-500' : 'bg-gray-300'}`}
                               title={new Date(day.date).toLocaleDateString()}
                             ></div>
                           ))
                         ) : (
                           Array.from({ length: 7 }, (_, i) => (
                             <div key={i} className="w-3 h-3 rounded-full bg-gray-300"></div>
                           ))
                         )}
                       </div>
                       <div className="text-xs text-gray-500 mt-2">Last 7 days</div>
                     </div>
                   </div>
                 </div>
               </div>
             </motion.div>
           </div>
                   )}

          {/* Assignment Submission Modal */}
          {showAssignmentSubmission && selectedAssignment && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                        Submit Assignment
                      </h2>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {selectedAssignment.title} • {selectedAssignment.course}
                      </p>
                    </div>
                    <button 
                      onClick={() => setShowAssignmentSubmission(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                
                <div className="p-6">
                  {/* Assignment Details */}
                  <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                      Assignment Details
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {selectedAssignment.description}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Due Date:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {selectedAssignment.dueDate}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Total Points:</span>
                        <span className="ml-2 font-medium text-gray-900 dark:text-white">
                          {selectedAssignment.totalPoints}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* File Upload Section */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Upload Files
                    </h3>
                    
                    {/* Drag & Drop Zone */}
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center hover:border-primary-500 transition-colors">
                      <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 dark:text-gray-400 mb-2">
                        Drag and drop files here, or click to browse
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-500 mb-4">
                        Supported formats: PDF, DOC, DOCX, TXT, ZIP, RAR (Max 10MB per file)
                      </p>
                      <input
                        type="file"
                        multiple
                        onChange={handleFileUpload}
                        className="hidden"
                        id="file-upload"
                        accept=".pdf,.doc,.docx,.txt,.zip,.rar,.jpg,.jpeg,.png"
                      />
                      <label
                        htmlFor="file-upload"
                        className="btn-primary cursor-pointer inline-flex items-center"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Choose Files
                      </label>
                    </div>

                    {/* Uploaded Files List */}
                    {submissionFiles.length > 0 && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 dark:text-white mb-3">
                          Uploaded Files ({submissionFiles.length})
                        </h4>
                        <div className="space-y-2">
                          {submissionFiles.map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                            >
                              <div className="flex items-center space-x-3">
                                <FileText className="w-5 h-5 text-gray-500" />
                                <div>
                                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {file.name}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    {formatFileSize(file.size)}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => removeFile(file.id)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Text Submission */}
                  <div className="mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                      Additional Comments (Optional)
                    </h3>
                    <textarea
                      value={submissionText}
                      onChange={(e) => setSubmissionText(e.target.value)}
                      placeholder="Add any additional comments, explanations, or notes about your submission..."
                      className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none"
                    />
                  </div>

                  {/* Submission Guidelines */}
                  <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                      Submission Guidelines
                    </h4>
                    <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                      <li>• Ensure all files are properly named and organized</li>
                      <li>• Check that your submission meets all requirements</li>
                      <li>• You can submit multiple files if needed</li>
                      <li>• Once submitted, you cannot edit your submission</li>
                      <li>• You will receive a confirmation email after submission</li>
                    </ul>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={() => setShowAssignmentSubmission(false)}
                      className="btn-secondary"
                      disabled={isSubmitting}
                    >
                      Cancel
                    </button>
                    <div className="flex items-center space-x-3">
                      <span className="text-sm text-gray-500">
                        {submissionFiles.length} file(s) selected
                      </span>
                      <button
                        onClick={() => submitAssignment(selectedAssignment)}
                        disabled={isSubmitting || submissionFiles.length === 0}
                        className={`btn-primary inline-flex items-center ${
                          isSubmitting || submissionFiles.length === 0
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4 mr-2" />
                            Submit Assignment
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
                     )}

           {/* New Post Modal */}
           {showNewPostModal && (
             <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
               <motion.div
                 initial={{ scale: 0.9, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
               >
                 <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                   <div className="flex items-center justify-between">
                     <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                       Create New Post
                     </h2>
                     <button 
                       onClick={() => setShowNewPostModal(false)}
                       className="text-gray-400 hover:text-gray-600"
                     >
                       <X className="w-4 h-4" />
                     </button>
                   </div>
                 </div>
                 
                 <div className="p-6">
                   <div className="space-y-4">
                     <div>
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                         Post Title *
                       </label>
                       <input
                         type="text"
                         value={newPostTitle}
                         onChange={(e) => setNewPostTitle(e.target.value)}
                         placeholder="Enter your post title..."
                         className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                       />
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                         Post Content *
                       </label>
                       <textarea
                         value={newPostContent}
                         onChange={(e) => setNewPostContent(e.target.value)}
                         placeholder="Write your post content here..."
                         rows={6}
                         className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white resize-none"
                       />
                     </div>
                     
                     <div>
                       <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                         Tags (optional)
                       </label>
                       <input
                         type="text"
                         value={newPostTags}
                         onChange={(e) => setNewPostTags(e.target.value)}
                         placeholder="Enter tags separated by commas (e.g., help, question, lesson-5)"
                         className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 dark:bg-gray-700 dark:text-white"
                       />
                       <p className="text-xs text-gray-500 mt-1">
                         Tags help others find your post easily
                       </p>
                     </div>
                   </div>
                   
                   <div className="flex items-center justify-end space-x-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                     <button
                       onClick={() => setShowNewPostModal(false)}
                       className="btn-secondary"
                     >
                       Cancel
                     </button>
                     <button
                       onClick={createNewPost}
                       disabled={!newPostTitle.trim() || !newPostContent.trim()}
                       className={`btn-primary ${
                         !newPostTitle.trim() || !newPostContent.trim()
                           ? 'opacity-50 cursor-not-allowed'
                           : ''
                       }`}
                     >
                       Create Post
                     </button>
                   </div>
                 </div>
               </motion.div>
             </div>
           )}

           {/* Video Player Modal */}
           {showVideoPlayer && currentLecture && (
             <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
               <motion.div
                 initial={{ scale: 0.9, opacity: 0 }}
                 animate={{ scale: 1, opacity: 1 }}
                 className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden"
               >
                 <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                   <div className="flex items-center justify-between">
                     <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                       {currentLecture.title}
                     </h2>
                     <button 
                       onClick={closeVideoPlayer}
                       className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                     >
                       <X className="w-5 h-5" />
                     </button>
                   </div>
                 </div>
                 
                 <div className="p-4">
                   <VideoPlayer
                     src={currentLecture.videoUrl}
                     poster={currentLecture.thumbnail || currentLecture.poster}
                     title={currentLecture.title}
                     onTimeUpdate={handleTimeUpdate}
                     onEnded={() => {
                       handleVideoEnd();
                       handleVideoComplete(currentLecture.id);
                     }}
                     onBookmark={handleAddBookmark}
                     bookmarks={lectureBookmarks}
                     className="aspect-video"
                     initialVolume={0.7}
                     autoPlay={true}
                     attachments={currentLecture.attachments || []}
                     lectureId={currentLecture.id}
                     onDiscussionToggle={setShowDiscussion}
                     showDiscussion={showDiscussion}
                     currentUser={currentUser}
                   />
                   
                   <div className="mt-4">
                     <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                       Lecture Details
                     </h3>
                     <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                       <div>
                         <p className="text-gray-600 dark:text-gray-400">Duration</p>
                         <p className="font-medium text-gray-900 dark:text-white">
                           {currentLecture.duration ? `${currentLecture.duration} minutes` : 'N/A'}
                         </p>
                       </div>
                       <div>
                         <p className="text-gray-600 dark:text-gray-400">Type</p>
                         <p className="font-medium text-gray-900 dark:text-white capitalize">
                           {currentLecture.type || 'Video'}
                         </p>
                       </div>
                       {currentLecture.description && (
                         <div className="md:col-span-2">
                           <p className="text-gray-600 dark:text-gray-400">Description</p>
                           <p className="font-medium text-gray-900 dark:text-white">
                             {currentLecture.description}
                           </p>
                         </div>
                       )}
                     </div>
                   </div>
                 </div>
               </motion.div>
             </div>
           )}
       
          {showPaymentModal && (
            <SmartPaymentModal
              isOpen={showPaymentModal}
              onClose={() => setShowPaymentModal(false)}
              paymentData={paymentData}
              onSuccess={handlePaymentSuccess}
              onFailure={handlePaymentFailure}
              title="Complete Course Enrollment Payment"
            />
          )}
        </div>
   );
 };

export default Courses;
